<template>
  <!-- id is needed to get element's height in mini filters component -->
  <div
    id="resultsHeader"
    class="flex w-full items-center space-x-3 px-4 py-2"
  >
    <FreeBackButton @click="emit('onReturnButtonClick')" />
    <div
      role="button"
      tabindex="0"
      class="bg-background-neutral-secondary border-situational-lightgrey flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-2xl border px-3 py-1.5"
      @click="emit('onChangeParamsClick')"
    >
      <div class="relative flex min-w-0 flex-1 flex-col items-start">
        <div
          class="text-text-neutral-primary flex w-full items-center gap-1 text-left font-semibold"
        >
          <template v-if="isMultiTripSearch && directionParts.arr">
            <span class="truncate">{{ directionParts.dep }}</span>
            <RouteIcon class="text-text-neutral-primary h-5 w-5 shrink-0" />
            <span class="min-w-0 flex-1 truncate">{{
              directionParts.arr
            }}</span>
          </template>
          <span v-else class="min-w-0 truncate">{{ directionText }}</span>
        </div>
        <div
          ref="lineRef"
          class="text-text-neutral-quaternary w-full truncate text-left text-sm"
        >
          {{ lineText }}
        </div>
        <span
          ref="measureRef"
          class="text-text-neutral-quaternary invisible absolute left-0 top-0 whitespace-nowrap text-left text-sm"
          aria-hidden="true"
        >
          {{ fullLineText }}
        </span>
      </div>
      <EditIcon class="text-text-positive-primary shrink-0" />
    </div>
    <div class="flex shrink-0 items-center gap-2">
      <SharePath
        :title="`${directionText} (${lineText})`"
        :path-for-deeplink="pathForDeeplink"
      />
      <FavoriteButton
        v-if="!isMultiTripSearch"
        id="favorites-search-button"
        :is-active="isFavorite"
        :disabled="isFavoriteDisabled"
        variant="icon"
        @toggle="emit('onFavoriteToggle')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';
  import { FreeBackButton } from 'freedom-shared';
  import { resultsHeaderState } from 'shared';
  import type { Dict } from '@aviata/platform-types';

  import { SharePath } from '@/features/share';
  import { FavoriteButton } from '@/features/favorites';

  import RouteIcon from '@/assets/images/multi-trip/route-icon-20x20.svg?component';

  import EditIcon from '../assets/edit-icon.svg?component';

  const emit = defineEmits<{
    onReturnButtonClick: [];
    onChangeParamsClick: [];
    onFavoriteToggle: [];
  }>();

  const props = defineProps<{
    dict: Dict.Entity;
    searchParams: resultsHeaderState.types.SearchParameters;
    isFavorite?: boolean;
    isFavoriteDisabled?: boolean;
    isMultiTripSearch?: boolean;
  }>();

  const { t } = useI18n();
  const { getPassengersText } = resultsHeaderState.model.usePassengersText();
  const route = useRoute();

  const directionText = computed(() =>
    resultsHeaderState.lib.getDirectionText(props.searchParams, props.dict),
  );
  const directionParts = computed(() => {
    const [dep = directionText.value, arr = ''] = directionText.value.split(
      ' – ',
      2,
    );
    return { dep, arr };
  });
  const datesText = computed(() =>
    resultsHeaderState.lib.getExtendedDatesText(props.searchParams),
  );
  const passengersText = computed(() => getPassengersText(props.searchParams));

  const passengersCount = computed(() => {
    const {
      adults = 0,
      children = 0,
      infants = 0,
      youth = 0,
    } = props.searchParams;
    return adults + children + infants + youth;
  });

  const pathForDeeplink = computed(() => {
    const { hashCode } = route.params;
    return `/search/${hashCode}/deep_link`;
  });

  const shortPassengersText = computed(() =>
    t('passengersData.passenger', { count: passengersCount.value }),
  );

  const useShortPassengersText = ref(false);

  const displayedPassengersText = computed(() =>
    useShortPassengersText.value
      ? shortPassengersText.value
      : passengersText.value,
  );

  const lineText = computed(
    () => `${datesText.value}, ${displayedPassengersText.value}`,
  );
  const fullLineText = computed(
    () => `${datesText.value}, ${passengersText.value}`,
  );

  const lineRef = ref<HTMLElement | null>(null);
  const measureRef = ref<HTMLElement | null>(null);

  /**
   * Check if the text overflows the container and if it does, use the short passengers text
   */
  const checkOverflow = () => {
    if (!lineRef.value || !measureRef.value) {
      return;
    }

    const availableWidth = lineRef.value.clientWidth;
    const fullTextWidth = measureRef.value.scrollWidth;

    useShortPassengersText.value = fullTextWidth > availableWidth;
  };

  let resizeObserver: ResizeObserver | null = null;

  onMounted(async () => {
    await nextTick();
    checkOverflow();

    if (!lineRef.value) {
      return;
    }

    resizeObserver = new ResizeObserver(() => checkOverflow());
    resizeObserver.observe(lineRef.value);
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });

  watch(fullLineText, async () => {
    await nextTick();
    checkOverflow();
  });
</script>
